import homeRuntimeArt from './home-runtime-art.json' with { type: 'json' };
import {
  SETTLEMENT_ISLAND_ART,
  SETTLEMENT_ISLAND_DISTRICTS,
  type IslandCoverRectangle,
  type IslandPoint,
  type IslandQuad,
} from './settlement-island-registration';

/** These landings require visual confirmation against the matching natural-shore raster set. */
export const SETTLEMENT_BRIDGE_CONTACT_IDENTITY = 'natural-shores-v1';
/** Nominal width: 22 / 32 = 0.6875 ground cells, not a constant screen-normal width. */
export const SETTLEMENT_BRIDGE_WIDTH = 22;

/** Flat deck only: projecting this raster never tilts an upright railing or post. */
export const settlementBridgeDeckManifest = {
  id: 'settlement_timber_bridge_deck_v1',
  url: homeRuntimeArt['timber-bridge-deck-v1'].url,
  sourceUrl: homeRuntimeArt['timber-bridge-deck-v1'].sourceUrl,
  width: 1536,
  height: 1024,
  sourceQuad: [
    { x: 60, y: 340 },
    { x: 1476, y: 340 },
    { x: 1476, y: 674 },
    { x: 60, y: 674 },
  ] as IslandQuad,
  reviewStatus: 'needs_cultural_review',
} as const;

/**
 * Garden already joins the original southwest island at its northern dry shoulder.
 * The basin's authored bridge connects that island to the primary shore. A second deck
 * from (500,520) to (438,673) forked off that bridge and looked like a steep unsupported ramp.
 * Preserve the complete authored connection, including its supports and perspective.
 */
export const settlementAuthoredIslandConnections = {
  garden_quarter: {
    environment: SETTLEMENT_ISLAND_ART.source,
    shore: 'natural-shore-garden-v1',
    bridgeEndpoints: [
      { x: 500, y: 520 },
      { x: 350, y: 575 },
    ],
    dryApproach: [
      { x: 300, y: 605 },
      { x: 300, y: 650 },
    ],
  },
} as const;

/** Original 1672×941 source coordinates for additional, runtime-only connections. */
export const settlementIslandBridgeContacts = {
  artisan_quarter: {
    primary: { x: 560, y: 367 },
    expansion: { x: 493, y: 341 },
    basis: 'inset onto original northwest stone landing and expansion dry southeast shoulder',
  },
  market_quarter: {
    primary: { x: 1092, y: 365 },
    expansion: { x: 1218, y: 418 },
    basis: 'original northeast stone bridge approach toward expansion southwest dry shoulder',
  },
  ceremonial_quarter: {
    primary: { x: 1135, y: 638 },
    expansion: { x: 1182, y: 666 },
    basis: 'existing primary foreground pier tip toward the expansion northwest shore',
  },
} as const;

type BridgeDistrict = keyof typeof settlementIslandBridgeContacts;

export interface SettlementIslandBridge {
  districtCode: BridgeDistrict;
  contactIdentity: typeof SETTLEMENT_BRIDGE_CONTACT_IDENTITY;
  /** Source points are already deck-level contacts, not cliff-bottom/waterline positions. */
  sourceEndpoints: readonly [IslandPoint, IslandPoint];
  /** Along-deck order: primary-left, expansion-left, expansion-right, primary-right. */
  sourceDeckQuad: IslandQuad;
  /** Length of the projected cross-deck edge in original-image pixels; depends on heading. */
  sourceWidth: number;
  groundWidthCells: number;
  sourceLength: number;
  endpoints: readonly [IslandPoint, IslandPoint];
  deckQuad: IslandQuad;
  width: number;
  /** A single 2D raster does not establish physical elevation; do not invent a ramp height. */
  endpointGroundHeights: readonly [null, null];
  contactBasis: string;
}

export type SettlementIslandBridgeDecision =
  | { status: 'hidden'; reason: 'locked' | 'unsupported_district' | 'authored_connection' }
  | {
      status: 'blocked';
      reason:
        'missing_land_contact' | 'invalid_cover' | 'invalid_contact' | 'legal_plot_intersection';
    }
  | { status: 'ready'; bridge: SettlementIslandBridge };

/** Separating axes cover crossing edges and containment, not just endpoint-in-plot checks. */
function convexQuadsIntersect(first: IslandQuad, second: IslandQuad): boolean {
  for (const quad of [first, second]) {
    for (let index = 0; index < 4; index++) {
      const a = quad[index]!;
      const b = quad[(index + 1) % 4]!;
      const axis = { x: -(b.y - a.y), y: b.x - a.x };
      const project = (point: IslandPoint) => point.x * axis.x + point.y * axis.y;
      const firstRange = first.map(project);
      const secondRange = second.map(project);
      if (
        Math.max(...firstRange) < Math.min(...secondRange) - 1e-7 ||
        Math.max(...secondRange) < Math.min(...firstRange) - 1e-7
      )
        return false;
    }
  }
  return true;
}

/**
 * Reviewed identity is an explicit caller-owned art-review decision, not inferred from unlock.
 * Until both shore contacts have been inspected against that exact raster set, omit it and the
 * result reports missing_land_contact. Geometry checks alone cannot prove physical land contact.
 * This presentation helper never creates a building, reward, territory or collision rule.
 */
export function createSettlementIslandBridge(
  districtCode: string,
  unlocked: boolean,
  cover: IslandCoverRectangle,
  reviewedContactIdentity?: string,
): SettlementIslandBridgeDecision {
  if (Object.hasOwn(settlementAuthoredIslandConnections, districtCode))
    return { status: 'hidden', reason: unlocked === true ? 'authored_connection' : 'locked' };
  if (!Object.hasOwn(settlementIslandBridgeContacts, districtCode))
    return { status: 'hidden', reason: 'unsupported_district' };
  if (unlocked !== true) return { status: 'hidden', reason: 'locked' };
  if (reviewedContactIdentity !== SETTLEMENT_BRIDGE_CONTACT_IDENTITY)
    return { status: 'blocked', reason: 'missing_land_contact' };
  if (
    ![cover.x, cover.y, cover.width, cover.height, cover.scale].every(Number.isFinite) ||
    cover.scale <= 0 ||
    Math.abs(cover.width - SETTLEMENT_ISLAND_ART.width * cover.scale) > 1e-6 ||
    Math.abs(cover.height - SETTLEMENT_ISLAND_ART.height * cover.scale) > 1e-6
  )
    return { status: 'blocked', reason: 'invalid_cover' };

  const code = districtCode as BridgeDistrict;
  const contact = settlementIslandBridgeContacts[code];
  const primary = { ...contact.primary };
  const expansion = { ...contact.expansion };
  const dx = expansion.x - primary.x;
  const dy = expansion.y - primary.y;
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length < SETTLEMENT_BRIDGE_WIDTH || length > 180)
    return { status: 'blocked', reason: 'invalid_contact' };
  // Invert the common ground basis (32, 11), (-32, 11), then take the perpendicular
  // in that plane. A screen-space perpendicular makes shallow decks resemble upright fences.
  const tangentA = (dx / 32 + dy / 11) / 2;
  const tangentB = (dy / 11 - dx / 32) / 2;
  const groundLength = Math.hypot(tangentA, tangentB);
  const groundWidthCells = SETTLEMENT_BRIDGE_WIDTH / 32;
  const normalA = ((-tangentB / groundLength) * groundWidthCells) / 2;
  const normalB = ((tangentA / groundLength) * groundWidthCells) / 2;
  const offset = { x: 32 * (normalA - normalB), y: 11 * (normalA + normalB) };
  const sourceWidth = 2 * Math.hypot(offset.x, offset.y);
  const sourceDeckQuad: IslandQuad = [
    { x: primary.x + offset.x, y: primary.y + offset.y },
    { x: expansion.x + offset.x, y: expansion.y + offset.y },
    { x: expansion.x - offset.x, y: expansion.y - offset.y },
    { x: primary.x - offset.x, y: primary.y - offset.y },
  ];
  if (
    !sourceDeckQuad.every(
      (point) =>
        Number.isFinite(point.x) &&
        Number.isFinite(point.y) &&
        point.x >= 0 &&
        point.y >= 0 &&
        point.x <= SETTLEMENT_ISLAND_ART.width &&
        point.y <= SETTLEMENT_ISLAND_ART.height,
    )
  )
    return { status: 'blocked', reason: 'invalid_contact' };
  if (
    Object.values(SETTLEMENT_ISLAND_DISTRICTS).some(({ sourceQuad }) =>
      convexQuadsIntersect(sourceDeckQuad, sourceQuad),
    )
  )
    return { status: 'blocked', reason: 'legal_plot_intersection' };

  const project = (point: IslandPoint): IslandPoint => ({
    x: cover.x + point.x * cover.scale,
    y: cover.y + point.y * cover.scale,
  });
  const deckQuad: IslandQuad = [
    project(sourceDeckQuad[0]),
    project(sourceDeckQuad[1]),
    project(sourceDeckQuad[2]),
    project(sourceDeckQuad[3]),
  ];
  const endpoints: readonly [IslandPoint, IslandPoint] = [project(primary), project(expansion)];
  const twiceTriangleArea =
    (deckQuad[1].x - deckQuad[0].x) * (deckQuad[2].y - deckQuad[0].y) -
    (deckQuad[1].y - deckQuad[0].y) * (deckQuad[2].x - deckQuad[0].x);
  if (
    ![...deckQuad, ...endpoints].every(
      (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
    ) ||
    !Number.isFinite(twiceTriangleArea) ||
    twiceTriangleArea === 0
  )
    return { status: 'blocked', reason: 'invalid_cover' };
  return {
    status: 'ready',
    bridge: {
      districtCode: code,
      contactIdentity: SETTLEMENT_BRIDGE_CONTACT_IDENTITY,
      sourceEndpoints: [primary, expansion],
      sourceDeckQuad,
      sourceWidth,
      groundWidthCells,
      sourceLength: length,
      endpoints,
      deckQuad,
      width: sourceWidth * cover.scale,
      endpointGroundHeights: [null, null],
      contactBasis: contact.basis,
    },
  };
}
